import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { colors, fonts } from '../brand';

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('submitted');
  const [qa, setQa] = useState({});
  const [phases, setPhases] = useState([]);
  const [submittedQuestions, setSubmittedQuestions] = useState([]);
  const [refugeInterest, setRefugeInterest] = useState([]);
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [resources, setResources] = useState([]);
  const [siteFeedback, setSiteFeedback] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState('');
  const [addingAnnouncement, setAddingAnnouncement] = useState(false);
  const [voiceMemos, setVoiceMemos] = useState([]);
  const [memoAudioUrls, setMemoAudioUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [editingQA, setEditingQA] = useState(null);
  const [editingPhase, setEditingPhase] = useState(null);
  const [editingTerm, setEditingTerm] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [addingQA, setAddingQA] = useState(false);
  const [addingTerm, setAddingTerm] = useState(false);
  const [addingResource, setAddingResource] = useState(false);
  const [newQA, setNewQA] = useState({ category: '', question: '', answer: '', order: 0 });
  const [newTerm, setNewTerm] = useState({ term: '', definition: '' });
  const [newResource, setNewResource] = useState({ category: '', category_description: '', name: '', url: '', description: '' });
  const [seedMsg, setSeedMsg] = useState('');
  const [glossarySeedMsg, setGlossarySeedMsg] = useState('');
  const [resourceSeedMsg, setResourceSeedMsg] = useState('');

  useEffect(() => {
    if (user?.email !== 'amyelizabeth2021@gmail.com') {
      navigate('/dashboard');
      return;
    }
    loadContent();
  }, [user, token, navigate]);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadContent = async () => {
    try {
      const [qaRes, phaseRes, submittedRes, refugeRes, glossaryRes, resourcesRes, feedbackRes, contactRes, annRes, memosRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/admin/qa`, auth),
        axios.get(`${API}/api/admin/roadmap`, auth),
        axios.get(`${API}/api/admin/submitted-questions`, auth),
        axios.get(`${API}/api/admin/refuge-interest`, auth),
        axios.get(`${API}/api/admin/glossary`, auth),
        axios.get(`${API}/api/admin/resources`, auth),
        axios.get(`${API}/api/admin/feedback`, auth),
        axios.get(`${API}/api/admin/contact-messages`, auth),
        axios.get(`${API}/api/admin/announcements`, auth),
        axios.get(`${API}/api/admin/voice-memos`, auth),
        axios.get(`${API}/api/admin/users`, auth),
      ]);
      setQa(qaRes.data.qa);
      setPhases(phaseRes.data.phases);
      setSubmittedQuestions(submittedRes.data.questions);
      setRefugeInterest(refugeRes.data.entries);
      setGlossaryTerms(glossaryRes.data.terms);
      setResources(resourcesRes.data.resources);
      setSiteFeedback(feedbackRes.data.feedback);
      setContactMessages(contactRes.data.messages);
      setAnnouncements(annRes.data.announcements);
      setVoiceMemos(memosRes.data.submissions);
      setMembers(usersRes.data.users);
    } catch (e) {
      console.error('Failed to load content:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQA = async (qaItem) => {
    try {
      if (qaItem.id) {
        await axios.put(`${API}/api/admin/qa/${qaItem.id}`, qaItem, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/api/admin/qa`, qaItem, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setEditingQA(null);
      loadContent();
    } catch (e) {
      console.error('Failed to save Q&A:', e);
    }
  };

  const handleAddQA = async () => {
    try {
      await axios.post(`${API}/api/admin/qa`, newQA, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddingQA(false);
      setNewQA({ category: '', question: '', answer: '', order: 0 });
      loadContent();
    } catch (e) {
      console.error('Failed to add Q&A:', e);
    }
  };

  const handleSeed = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeedMsg(res.data.message);
      loadContent();
    } catch (e) {
      setSeedMsg('Seed failed — check console.');
    }
  };

  const handleSeedGlossary = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/glossary/seed`, {}, auth);
      setGlossarySeedMsg(res.data.message);
      loadContent();
    } catch { setGlossarySeedMsg('Seed failed.'); }
  };

  const handleSeedResources = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/resources/seed`, {}, auth);
      setResourceSeedMsg(res.data.message);
      loadContent();
    } catch { setResourceSeedMsg('Seed failed.'); }
  };

  const handleSaveTerm = async (item) => {
    try {
      if (item.id) {
        await axios.put(`${API}/api/admin/glossary/${item.id}`, item, auth);
      } else {
        await axios.post(`${API}/api/admin/glossary`, item, auth);
      }
      setEditingTerm(null);
      setAddingTerm(false);
      setNewTerm({ term: '', definition: '' });
      loadContent();
    } catch (e) { console.error(e); }
  };

  const handleDeleteTerm = async (id) => {
    if (window.confirm('Delete this term?')) {
      await axios.delete(`${API}/api/admin/glossary/${id}`, auth);
      loadContent();
    }
  };

  const handleSaveResource = async (item) => {
    try {
      if (item.id) {
        await axios.put(`${API}/api/admin/resources/${item.id}`, item, auth);
      } else {
        await axios.post(`${API}/api/admin/resources`, item, auth);
      }
      setEditingResource(null);
      setAddingResource(false);
      setNewResource({ category: '', category_description: '', name: '', url: '', description: '' });
      loadContent();
    } catch (e) { console.error(e); }
  };

  const handleDeleteResource = async (id) => {
    if (window.confirm('Delete this resource?')) {
      await axios.delete(`${API}/api/admin/resources/${id}`, auth);
      loadContent();
    }
  };

  const handleDeleteQA = async (qaId) => {
    if (window.confirm('Delete this Q&A?')) {
      try {
        await axios.delete(`${API}/api/admin/qa/${qaId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        loadContent();
      } catch (e) {
        console.error('Failed to delete Q&A:', e);
      }
    }
  };

  const handleSavePhase = async (phase) => {
    try {
      await axios.put(`${API}/api/admin/roadmap/${phase.phase}`, phase, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingPhase(null);
      loadContent();
    } catch (e) {
      console.error('Failed to save phase:', e);
    }
  };

  if (loading) return <div style={styles.loadingPage}>Loading...</div>;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Admin Dashboard</h1>
        <button style={styles.backBtn} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </header>

      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeTab === 'announcements' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('announcements')}
        >
          What's New
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'submitted' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('submitted')}
        >
          Submitted Questions {submittedQuestions.filter(q => !q.reviewed).length > 0 && `(${submittedQuestions.filter(q => !q.reviewed).length} new)`}
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'contact' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('contact')}
        >
          Contact & Calls {contactMessages.filter(m => !m.reviewed).length > 0 && `(${contactMessages.filter(m => !m.reviewed).length} new)`}
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'qa' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('qa')}
        >
          Q&amp;A Management
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'voicememos' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('voicememos')}
        >
          Voice Memos {voiceMemos.filter(m => !m.reviewed).length > 0 && `(${voiceMemos.filter(m => !m.reviewed).length} new)`}
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'feedback' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('feedback')}
        >
          Feedback {siteFeedback.filter(f => !f.reviewed).length > 0 && `(${siteFeedback.filter(f => !f.reviewed).length} new)`}
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'glossary' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('glossary')}
        >
          Glossary
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'resources' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('resources')}
        >
          Resources
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'refuge' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('refuge')}
        >
          Refuge Interest ({refugeInterest.length})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'members' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('members')}
        >
          Members ({members.length})
        </button>
        <button
          style={{ ...styles.tab, ...(activeTab === 'tools' ? styles.tabActive : {}) }}
          onClick={() => setActiveTab('tools')}
        >
          Tools
        </button>
      </div>

      <main style={styles.main}>
        {activeTab === 'submitted' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Submitted Questions</h2>
            {submittedQuestions.length === 0 ? (
              <p style={styles.emptyNote}>No questions submitted yet.</p>
            ) : (
              <div style={styles.submittedList}>
                {submittedQuestions.map(q => (
                  <div key={q.id} style={{ ...styles.submittedItem, opacity: q.reviewed ? 0.5 : 1 }}>
                    <div style={styles.submittedMeta}>
                      <span style={styles.submittedUser}>{q.user_name || 'Member'} · {q.user_email}</span>
                      <span style={styles.submittedDate}>{new Date(q.submitted_at).toLocaleDateString()}</span>
                    </div>
                    <p style={styles.submittedQ}>{q.question}</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {!q.reviewed && (
                        <button style={styles.reviewedBtn} onClick={async () => {
                          await axios.put(`${API}/api/admin/submitted-questions/${q.id}/reviewed`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setSubmittedQuestions(prev => prev.map(x => x.id === q.id ? { ...x, reviewed: true } : x));
                        }}>
                          Mark as Reviewed
                        </button>
                      )}
                      <button style={styles.freeBtn} onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/api/admin/grant-free-membership`, { type: 'qa', id: q.id }, auth);
                          alert(res.data.message);
                        } catch (e) {
                          alert(e.response?.data?.error || 'Failed to grant membership');
                        }
                      }}>
                        Grant Free Membership
                      </button>
                    </div>
                    {q.reviewed && <span style={styles.reviewedBadge}>Reviewed</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'announcements' && (
          <div style={styles.section}>
            <div style={styles.qaHeader}>
              <h2 style={styles.sectionTitle}>What's New</h2>
              <button style={styles.addBtn} onClick={() => setAddingAnnouncement(!addingAnnouncement)}>
                {addingAnnouncement ? 'Cancel' : '+ Post Update'}
              </button>
            </div>
            <p style={styles.emptyNote}>These appear on the member dashboard. Keep them short — one line is best.</p>
            {addingAnnouncement && (
              <div style={styles.addForm}>
                <textarea
                  style={styles.textarea}
                  placeholder="e.g. 5 new Q&amp;A answers added this week — check the Q&amp;A tab."
                  rows={2}
                  value={newAnnouncement}
                  onChange={e => setNewAnnouncement(e.target.value)}
                />
                <div style={styles.buttonGroup}>
                  <button style={styles.saveBtn} disabled={!newAnnouncement.trim()} onClick={async () => {
                    await axios.post(`${API}/api/admin/announcements`, { message: newAnnouncement }, auth);
                    setNewAnnouncement('');
                    setAddingAnnouncement(false);
                    loadContent();
                  }}>Post</button>
                  <button style={styles.cancelBtn} onClick={() => setAddingAnnouncement(false)}>Cancel</button>
                </div>
              </div>
            )}
            {announcements.length === 0 ? (
              <p style={styles.emptyNote}>No announcements yet.</p>
            ) : (
              <div style={styles.submittedList}>
                {announcements.map(a => (
                  <div key={a.id} style={{ ...styles.submittedItem, borderLeftColor: colors.gold }}>
                    <div style={styles.submittedMeta}>
                      <span style={styles.submittedDate}>{new Date(a.created_at).toLocaleDateString()}</span>
                    </div>
                    <p style={styles.submittedQ}>{a.message}</p>
                    <button style={styles.deleteBtn} onClick={async () => {
                      if (window.confirm('Delete this announcement?')) {
                        await axios.delete(`${API}/api/admin/announcements/${a.id}`, auth);
                        loadContent();
                      }
                    }}>Delete</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Contact Messages & Call Requests — {contactMessages.length} total</h2>
            {contactMessages.length === 0 ? (
              <p style={styles.emptyNote}>No messages yet.</p>
            ) : (
              <div style={styles.submittedList}>
                {contactMessages.map(m => (
                  <div key={m.id} style={{ ...styles.submittedItem, opacity: m.reviewed ? 0.5 : 1, borderLeftColor: m.is_call_request ? colors.gold : colors.rose }}>
                    <div style={styles.submittedMeta}>
                      <span style={styles.submittedUser}>
                        {m.is_call_request && <strong style={{ color: colors.gold, marginRight: '8px' }}>📞 Call Request</strong>}
                        {m.name} · {m.email}
                      </span>
                      <span style={styles.submittedDate}>{new Date(m.submitted_at).toLocaleDateString()}</span>
                    </div>
                    <p style={styles.submittedQ}>{m.message}</p>
                    {!m.reviewed && (
                      <button style={styles.reviewedBtn} onClick={async () => {
                        await axios.put(`${API}/api/admin/contact-messages/${m.id}/reviewed`, {}, auth);
                        setContactMessages(prev => prev.map(x => x.id === m.id ? { ...x, reviewed: true } : x));
                      }}>
                        Mark as Reviewed
                      </button>
                    )}
                    {m.reviewed && <span style={styles.reviewedBadge}>Reviewed</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'voicememos' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Submitted Voice Memos — {voiceMemos.length} total</h2>
            {voiceMemos.length === 0 ? (
              <p style={styles.emptyNote}>No submissions yet.</p>
            ) : (
              <div style={styles.submittedList}>
                {voiceMemos.map(m => (
                  <div key={m.id} style={{ ...styles.submittedItem, opacity: m.reviewed ? 0.5 : 1 }}>
                    <div style={styles.submittedMeta}>
                      <span style={styles.submittedUser}>{m.submitter_name || 'Anonymous'}{m.submitter_email ? ` · ${m.submitter_email}` : ''}</span>
                      <span style={styles.submittedDate}>{new Date(m.submitted_at).toLocaleDateString()}</span>
                    </div>
                    <p style={styles.question}><strong>{m.title}</strong></p>
                    <p style={styles.answer}>{m.tagline}</p>
                    {memoAudioUrls[m.id] ? (
                      <audio controls src={memoAudioUrls[m.id]} style={{ width: '100%', marginTop: '8px' }} />
                    ) : (
                      <button style={styles.editBtn} onClick={async () => {
                        const res = await axios.get(`${API}/api/admin/voice-memos/${m.id}/url`, auth);
                        setMemoAudioUrls(prev => ({ ...prev, [m.id]: res.data.url }));
                      }}>Load Audio</button>
                    )}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {!m.reviewed && (
                        <button style={styles.reviewedBtn} onClick={async () => {
                          await axios.put(`${API}/api/admin/voice-memos/${m.id}/reviewed`, {}, auth);
                          setVoiceMemos(prev => prev.map(x => x.id === m.id ? { ...x, reviewed: true } : x));
                        }}>Mark as Reviewed</button>
                      )}
                      <button style={styles.freeBtn} onClick={async () => {
                        try {
                          const res = await axios.post(`${API}/api/admin/grant-free-membership`, { type: 'story', id: m.id }, auth);
                          alert(res.data.message);
                        } catch (e) {
                          alert(e.response?.data?.error || 'Failed to send invite');
                        }
                      }}>
                        Grant Free Membership
                      </button>
                    </div>
                    {m.reviewed && <span style={styles.reviewedBadge}>Reviewed</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'feedback' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Site Feedback — {siteFeedback.length} responses</h2>
            {siteFeedback.length === 0 ? (
              <p style={styles.emptyNote}>No feedback submitted yet.</p>
            ) : (
              <div style={styles.submittedList}>
                {siteFeedback.map(fb => (
                  <div key={fb.id} style={{ ...styles.submittedItem, opacity: fb.reviewed ? 0.5 : 1 }}>
                    <div style={styles.submittedMeta}>
                      <span style={styles.submittedUser}>{fb.name || 'Anonymous'}{fb.email ? ` · ${fb.email}` : ''}</span>
                      <span style={styles.submittedDate}>{new Date(fb.submitted_at).toLocaleDateString()}</span>
                    </div>
                    <p style={styles.submittedQ}>{fb.message}</p>
                    {!fb.reviewed && (
                      <button style={styles.reviewedBtn} onClick={async () => {
                        await axios.put(`${API}/api/admin/feedback/${fb.id}/reviewed`, {}, auth);
                        setSiteFeedback(prev => prev.map(x => x.id === fb.id ? { ...x, reviewed: true } : x));
                      }}>
                        Mark as Reviewed
                      </button>
                    )}
                    {fb.reviewed && <span style={styles.reviewedBadge}>Reviewed</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'glossary' && (
          <div style={styles.section}>
            <div style={styles.qaHeader}>
              <h2 style={styles.sectionTitle}>Glossary Terms ({glossaryTerms.length})</h2>
              <div style={styles.qaHeaderBtns}>
                {glossaryTerms.length === 0 && (
                  <button style={styles.seedBtn} onClick={handleSeedGlossary}>Seed from Defaults</button>
                )}
                {glossarySeedMsg && <span style={styles.seedMsg}>{glossarySeedMsg}</span>}
                <button style={styles.addBtn} onClick={() => setAddingTerm(!addingTerm)}>
                  {addingTerm ? 'Cancel' : '+ Add Term'}
                </button>
              </div>
            </div>
            {addingTerm && (
              <div style={styles.addForm}>
                <input style={styles.input} placeholder="Term" value={newTerm.term} onChange={e => setNewTerm({ ...newTerm, term: e.target.value })} />
                <textarea style={styles.textarea} placeholder="Definition" rows={4} value={newTerm.definition} onChange={e => setNewTerm({ ...newTerm, definition: e.target.value })} />
                <div style={styles.buttonGroup}>
                  <button style={styles.saveBtn} onClick={() => handleSaveTerm(newTerm)} disabled={!newTerm.term || !newTerm.definition}>Save</button>
                  <button style={styles.cancelBtn} onClick={() => setAddingTerm(false)}>Cancel</button>
                </div>
              </div>
            )}
            {glossaryTerms.map(item => (
              <div key={item.id} style={styles.qaItem}>
                {editingTerm?.id === item.id ? (
                  <div style={styles.editForm}>
                    <input style={styles.input} value={editingTerm.term} onChange={e => setEditingTerm({ ...editingTerm, term: e.target.value })} />
                    <textarea style={styles.textarea} rows={4} value={editingTerm.definition} onChange={e => setEditingTerm({ ...editingTerm, definition: e.target.value })} />
                    <div style={styles.buttonGroup}>
                      <button style={styles.saveBtn} onClick={() => handleSaveTerm(editingTerm)}>Save</button>
                      <button style={styles.cancelBtn} onClick={() => setEditingTerm(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p style={styles.question}><strong>{item.term}</strong></p>
                    <p style={styles.answer}>{item.definition}</p>
                    <div style={styles.actions}>
                      <button style={styles.editBtn} onClick={() => setEditingTerm(item)}>Edit</button>
                      <button style={styles.deleteBtn} onClick={() => handleDeleteTerm(item.id)}>Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'resources' && (
          <div style={styles.section}>
            <div style={styles.qaHeader}>
              <h2 style={styles.sectionTitle}>Resources ({resources.length})</h2>
              <div style={styles.qaHeaderBtns}>
                {resources.length === 0 && (
                  <button style={styles.seedBtn} onClick={handleSeedResources}>Seed from Defaults</button>
                )}
                {resourceSeedMsg && <span style={styles.seedMsg}>{resourceSeedMsg}</span>}
                <button style={styles.addBtn} onClick={() => setAddingResource(!addingResource)}>
                  {addingResource ? 'Cancel' : '+ Add Resource'}
                </button>
              </div>
            </div>
            {addingResource && (
              <div style={styles.addForm}>
                <input style={styles.input} placeholder="Category (e.g. Legal Aid & Pro Bono Services)" value={newResource.category} onChange={e => setNewResource({ ...newResource, category: e.target.value })} />
                <input style={styles.input} placeholder="Category description (shown under heading)" value={newResource.category_description} onChange={e => setNewResource({ ...newResource, category_description: e.target.value })} />
                <input style={styles.input} placeholder="Resource name" value={newResource.name} onChange={e => setNewResource({ ...newResource, name: e.target.value })} />
                <input style={styles.input} placeholder="URL (https://...)" value={newResource.url} onChange={e => setNewResource({ ...newResource, url: e.target.value })} />
                <textarea style={styles.textarea} placeholder="Description" rows={3} value={newResource.description} onChange={e => setNewResource({ ...newResource, description: e.target.value })} />
                <div style={styles.buttonGroup}>
                  <button style={styles.saveBtn} onClick={() => handleSaveResource(newResource)} disabled={!newResource.category || !newResource.name || !newResource.url}>Save</button>
                  <button style={styles.cancelBtn} onClick={() => setAddingResource(false)}>Cancel</button>
                </div>
              </div>
            )}
            {resources.map(item => (
              <div key={item.id} style={styles.qaItem}>
                {editingResource?.id === item.id ? (
                  <div style={styles.editForm}>
                    <input style={styles.input} placeholder="Category" value={editingResource.category} onChange={e => setEditingResource({ ...editingResource, category: e.target.value })} />
                    <input style={styles.input} placeholder="Category description" value={editingResource.category_description || ''} onChange={e => setEditingResource({ ...editingResource, category_description: e.target.value })} />
                    <input style={styles.input} placeholder="Name" value={editingResource.name} onChange={e => setEditingResource({ ...editingResource, name: e.target.value })} />
                    <input style={styles.input} placeholder="URL" value={editingResource.url} onChange={e => setEditingResource({ ...editingResource, url: e.target.value })} />
                    <textarea style={styles.textarea} placeholder="Description" rows={3} value={editingResource.description} onChange={e => setEditingResource({ ...editingResource, description: e.target.value })} />
                    <div style={styles.buttonGroup}>
                      <button style={styles.saveBtn} onClick={() => handleSaveResource(editingResource)}>Save</button>
                      <button style={styles.cancelBtn} onClick={() => setEditingResource(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p style={{ ...styles.question, fontSize: '12px', color: colors.rose, marginBottom: '4px' }}>{item.category}</p>
                    <p style={styles.question}><strong>{item.name}</strong> — <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: colors.rose, fontSize: '12px' }}>{item.url}</a></p>
                    <p style={styles.answer}>{item.description}</p>
                    <div style={styles.actions}>
                      <button style={styles.editBtn} onClick={() => setEditingResource(item)}>Edit</button>
                      <button style={styles.deleteBtn} onClick={() => handleDeleteResource(item.id)}>Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'refuge' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Refuge Interest — {refugeInterest.length} responses</h2>
            {refugeInterest.length === 0 ? (
              <p style={styles.emptyNote}>No responses yet.</p>
            ) : (
              <>
                <div style={styles.refugeSummary}>
                  <div style={styles.refugeStat}><span style={styles.refugeNum}>{refugeInterest.filter(r => r.need_it).length}</span><span style={styles.refugeLabel}>Need it now</span></div>
                  <div style={styles.refugeStat}><span style={styles.refugeNum}>{refugeInterest.filter(r => r.want_it).length}</span><span style={styles.refugeLabel}>Would want it</span></div>
                  <div style={styles.refugeStat}><span style={styles.refugeNum}>{new Set(refugeInterest.map(r => r.state).filter(Boolean)).size}</span><span style={styles.refugeLabel}>States represented</span></div>
                </div>
                <div style={styles.submittedList}>
                  {refugeInterest.map(r => (
                    <div key={r.id} style={styles.submittedItem}>
                      <div style={styles.submittedMeta}>
                        <span style={styles.submittedUser}>{r.city}{r.city && r.state ? ', ' : ''}{r.state}</span>
                        <span style={styles.submittedDate}>{new Date(r.submitted_at).toLocaleDateString()}</span>
                      </div>
                      <p style={styles.submittedQ}>
                        Needs it now: <strong>{r.need_it ? 'Yes' : 'No'}</strong> · Would want it: <strong>{r.want_it ? 'Yes' : 'No'}</strong>
                        {r.email ? ` · ${r.email}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'qa' && (
          <div style={styles.section}>
            <div style={styles.qaHeader}>
              <h2 style={styles.sectionTitle}>Q&amp;A Content</h2>
              <div style={styles.qaHeaderBtns}>
                <button style={styles.seedBtn} onClick={() => { if (window.confirm('This will replace ALL existing Q&A with the default content. Continue?')) handleSeed(); }}>Reseed from Defaults</button>
                {seedMsg && <span style={styles.seedMsg}>{seedMsg}</span>}
                <button style={styles.addBtn} onClick={() => setAddingQA(!addingQA)}>
                  {addingQA ? 'Cancel' : '+ Add New Q&A'}
                </button>
              </div>
            </div>

            {addingQA && (
              <div style={styles.addForm}>
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Category (e.g. Housing, Custody & Kids)"
                  value={newQA.category}
                  onChange={e => setNewQA({ ...newQA, category: e.target.value })}
                />
                <input
                  style={styles.input}
                  type="text"
                  placeholder="Question"
                  value={newQA.question}
                  onChange={e => setNewQA({ ...newQA, question: e.target.value })}
                />
                <textarea
                  style={styles.textarea}
                  placeholder="Answer"
                  rows={5}
                  value={newQA.answer}
                  onChange={e => setNewQA({ ...newQA, answer: e.target.value })}
                />
                <div style={styles.buttonGroup}>
                  <button style={styles.saveBtn} onClick={handleAddQA}
                    disabled={!newQA.category || !newQA.question || !newQA.answer}>
                    Save
                  </button>
                  <button style={styles.cancelBtn} onClick={() => setAddingQA(false)}>Cancel</button>
                </div>
              </div>
            )}

            {Object.keys(qa).map(category => (
              <div key={category} style={styles.categoryBlock}>
                <h3 style={styles.categoryTitle}>{category}</h3>
                {qa[category].map(item => (
                  <div key={item.id} style={styles.qaItem}>
                    {editingQA?.id === item.id ? (
                      <div style={styles.editForm}>
                        <input
                          type="text"
                          placeholder="Question"
                          value={editingQA.question}
                          onChange={(e) => setEditingQA({ ...editingQA, question: e.target.value })}
                          style={styles.input}
                        />
                        <textarea
                          placeholder="Answer"
                          value={editingQA.answer}
                          onChange={(e) => setEditingQA({ ...editingQA, answer: e.target.value })}
                          style={styles.textarea}
                          rows={5}
                        />
                        <div style={styles.buttonGroup}>
                          <button style={styles.saveBtn} onClick={() => handleSaveQA(editingQA)}>Save</button>
                          <button style={styles.cancelBtn} onClick={() => setEditingQA(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                          <p style={{ ...styles.question, margin: 0 }}><strong>Q:</strong> {item.q}</p>
                          {item.free && <span style={styles.freeBadge}>FREE</span>}
                        </div>
                        <p style={styles.answer}><strong>A:</strong> {item.a}</p>
                        <div style={styles.actions}>
                          <button style={styles.editBtn} onClick={() => setEditingQA({ ...item, question: item.q, answer: item.a })}>Edit</button>
                          <button style={styles.deleteBtn} onClick={() => handleDeleteQA(item.id)}>Delete</button>
                          <button
                            style={{ ...styles.editBtn, backgroundColor: item.free ? '#2a7a2a' : colors.textMedium }}
                            onClick={async () => {
                              await axios.put(`${API}/api/admin/qa/${item.id}`, { free: !item.free }, auth);
                              loadContent();
                            }}
                          >
                            {item.free ? 'Free ✓' : 'Make Free'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'roadmap' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Roadmap Phases</h2>
            {phases.map(phase => (
              <div key={phase.phase} style={styles.phaseBlock}>
                {editingPhase?.phase === phase.phase ? (
                  <div style={styles.editForm}>
                    <label>Title:</label>
                    <input
                      type="text"
                      value={editingPhase.title}
                      onChange={(e) => setEditingPhase({ ...editingPhase, title: e.target.value })}
                      style={styles.input}
                    />
                    <label>Note:</label>
                    <textarea
                      value={editingPhase.note}
                      onChange={(e) => setEditingPhase({ ...editingPhase, note: e.target.value })}
                      style={styles.textarea}
                      rows={3}
                    />
                    <label>Steps (one per line):</label>
                    <textarea
                      value={editingPhase.steps.join('\n')}
                      onChange={(e) => setEditingPhase({ ...editingPhase, steps: e.target.value.split('\n').filter(s => s.trim()) })}
                      style={styles.textarea}
                      rows={8}
                    />
                    <div style={styles.buttonGroup}>
                      <button style={styles.saveBtn} onClick={() => handleSavePhase(editingPhase)}>Save</button>
                      <button style={styles.cancelBtn} onClick={() => setEditingPhase(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h3 style={styles.phaseTitle}>Phase {phase.phase}: {phase.title}</h3>
                    <p style={styles.phaseNote}><em>{phase.note}</em></p>
                    <ul style={styles.stepsList}>
                      {phase.steps.map((step, i) => (
                        <li key={i} style={styles.step}>{step}</li>
                      ))}
                    </ul>
                    <button style={styles.editBtn} onClick={() => setEditingPhase(phase)}>Edit Phase</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'tools' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Admin Tools</h2>
            <div style={{ backgroundColor: colors.white, border: `1px solid ${colors.peach}`, padding: '28px', maxWidth: '400px' }}>
              <h3 style={{ fontFamily: fonts.heading, color: colors.navy, fontSize: '16px', marginBottom: '8px' }}>Reset My Quiz</h3>
              <p style={{ fontFamily: fonts.body, color: colors.textMedium, fontSize: '13px', lineHeight: '1.7', marginBottom: '20px' }}>Clears your quiz answers so you can re-test the onboarding flow from the beginning.</p>
              <button
                style={{ backgroundColor: colors.navy, color: colors.white, border: 'none', padding: '10px 24px', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600', cursor: 'pointer', letterSpacing: '0.5px' }}
                onClick={async () => {
                  await axios.post(`${API}/api/admin/reset-quiz`, {}, auth);
                  alert('Quiz reset. You\'ll be sent to the quiz next time you visit the dashboard.');
                }}
              >
                Reset My Quiz
              </button>
            </div>
          </div>
        )}

        {activeTab === 'members' && (
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Members ({members.length} total · {members.filter(m => m.subscription_status === 'active').length} active)</h2>
            <div style={styles.membersTable}>
              <div style={{ ...styles.memberRow, gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                <span style={{ ...styles.memberCell, fontWeight: '600', color: colors.navy }}>Email</span>
                <span style={{ ...styles.memberCell, fontWeight: '600', color: colors.navy }}>Name</span>
                <span style={{ ...styles.memberCell, fontWeight: '600', color: colors.navy }}>Status</span>
                <span style={{ ...styles.memberCell, fontWeight: '600', color: colors.navy }}>Quiz</span>
                <span style={{ ...styles.memberCell, fontWeight: '600', color: colors.navy }}></span>
              </div>
              {members.map(m => (
                <div key={m.id} style={{ ...styles.memberRow, gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                  <span style={styles.memberCell}>{m.email}</span>
                  <span style={styles.memberCell}>{m.first_name || '—'}</span>
                  <span style={{ ...styles.memberCell, color: m.subscription_status === 'active' ? '#2a7a2a' : colors.textMedium, fontWeight: m.subscription_status === 'active' ? '600' : 'normal' }}>
                    {m.subscription_status === 'active' ? 'Active' : 'Free'}
                  </span>
                  <span style={{ ...styles.memberCell, color: m.quiz_completed ? colors.navy : colors.textMedium }}>
                    {m.quiz_completed ? 'Done' : 'Not started'}
                  </span>
                  <span style={{ ...styles.memberCell, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {m.subscription_status !== 'active' && (
                      <button style={styles.activateBtn} onClick={async () => {
                        if (window.confirm(`Activate subscription for ${m.email}?`)) {
                          await axios.post(`${API}/api/admin/users/${m.id}/activate`, {}, auth);
                          setMembers(prev => prev.map(u => u.id === m.id ? { ...u, subscription_status: 'active' } : u));
                        }
                      }}>Activate</button>
                    )}
                    <button style={styles.deleteMemberBtn} onClick={async () => {
                      if (window.confirm(`Permanently delete ${m.email}?`)) {
                        await axios.delete(`${API}/api/admin/users/${m.id}`, auth);
                        setMembers(prev => prev.filter(u => u.id !== m.id));
                      }
                    }}>Delete</button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: colors.cream, fontFamily: fonts.body },
  header: { backgroundColor: colors.white, padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.peach}` },
  title: { fontFamily: fonts.heading, color: colors.navy, fontSize: '24px', margin: 0 },
  backBtn: { backgroundColor: colors.rose, color: colors.white, border: 'none', padding: '10px 20px', cursor: 'pointer', borderRadius: '2px', fontFamily: fonts.body },
  tabs: { display: 'flex', gap: '0', borderBottom: `2px solid ${colors.peach}`, backgroundColor: colors.white, padding: '0 40px' },
  tab: { padding: '12px 20px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', borderBottom: '3px solid transparent', marginBottom: '-2px' },
  tabActive: { color: colors.rose, fontWeight: '600', borderBottom: `3px solid ${colors.rose}` },
  main: { maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' },
  section: { marginBottom: '40px' },
  sectionTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '20px', marginBottom: '20px' },
  emptyNote: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '14px', fontStyle: 'italic' },
  refugeSummary: { display: 'flex', gap: '24px', marginBottom: '28px', flexWrap: 'wrap' },
  refugeStat: { backgroundColor: colors.white, border: `1px solid ${colors.peach}`, padding: '20px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minWidth: '120px' },
  refugeNum: { fontFamily: fonts.heading, color: colors.rose, fontSize: '32px', fontWeight: '600' },
  refugeLabel: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px', textAlign: 'center' },
  submittedList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  submittedItem: { backgroundColor: colors.white, border: `1px solid ${colors.peach}`, borderLeft: `4px solid ${colors.rose}`, padding: '20px 24px' },
  submittedMeta: { display: 'flex', justifyContent: 'space-between', marginBottom: '8px' },
  submittedUser: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px' },
  submittedDate: { fontFamily: fonts.body, color: colors.textMedium, fontSize: '12px' },
  submittedQ: { fontFamily: fonts.body, color: colors.navy, fontSize: '15px', lineHeight: '1.7', marginBottom: '12px' },
  reviewedBtn: { padding: '6px 16px', backgroundColor: colors.navy, color: colors.white, border: 'none', fontSize: '12px', fontFamily: fonts.body, cursor: 'pointer', letterSpacing: '0.3px' },
  freeBtn: { padding: '6px 16px', backgroundColor: colors.rose, color: colors.white, border: 'none', fontSize: '12px', fontFamily: fonts.body, cursor: 'pointer', letterSpacing: '0.3px', fontWeight: '600' },
  reviewedBadge: { fontFamily: fonts.body, fontSize: '12px', color: colors.textMedium, fontStyle: 'italic' },
  qaHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' },
  qaHeaderBtns: { display: 'flex', alignItems: 'center', gap: '12px' },
  addBtn: { backgroundColor: colors.navy, color: colors.white, border: 'none', padding: '8px 18px', cursor: 'pointer', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600' },
  seedBtn: { backgroundColor: colors.gold, color: colors.navy, border: 'none', padding: '8px 18px', cursor: 'pointer', fontSize: '13px', fontFamily: fonts.body, fontWeight: '600' },
  seedMsg: { fontFamily: fonts.body, fontSize: '12px', color: colors.textMedium, fontStyle: 'italic' },
  addForm: { backgroundColor: colors.white, border: `1px solid ${colors.peach}`, borderTop: `3px solid ${colors.navy}`, padding: '24px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' },
  categoryBlock: { backgroundColor: colors.white, padding: '20px', marginBottom: '20px', border: `1px solid ${colors.peach}` },
  categoryTitle: { fontFamily: fonts.heading, color: colors.rose, fontSize: '16px', marginTop: 0 },
  qaItem: { padding: '15px', borderBottom: `1px solid ${colors.peach}`, marginBottom: '10px' },
  question: { margin: '0 0 8px 0', fontSize: '14px', color: colors.navy },
  answer: { margin: '0 0 8px 0', fontSize: '13px', color: colors.textMedium, lineHeight: '1.6' },
  actions: { display: 'flex', gap: '10px', marginTop: '10px' },
  editBtn: { backgroundColor: colors.rose, color: colors.white, border: 'none', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontFamily: fonts.body },
  deleteBtn: { backgroundColor: '#c64545', color: colors.white, border: 'none', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontFamily: fonts.body },
  phaseBlock: { backgroundColor: colors.white, padding: '20px', marginBottom: '20px', border: `1px solid ${colors.peach}` },
  phaseTitle: { fontFamily: fonts.heading, color: colors.navy, fontSize: '16px', marginTop: 0 },
  phaseNote: { color: colors.textMedium, fontSize: '13px', marginBottom: '10px' },
  stepsList: { listStyle: 'none', padding: '0', margin: '10px 0', fontSize: '13px', color: colors.textMedium },
  step: { padding: '6px 0', lineHeight: '1.6' },
  editForm: { display: 'flex', flexDirection: 'column', gap: '10px' },
  input: { padding: '8px', border: `1px solid ${colors.peach}`, fontFamily: fonts.body, fontSize: '13px' },
  textarea: { padding: '8px', border: `1px solid ${colors.peach}`, fontFamily: fonts.body, fontSize: '13px', resize: 'vertical' },
  buttonGroup: { display: 'flex', gap: '10px' },
  saveBtn: { backgroundColor: colors.rose, color: colors.white, border: 'none', padding: '10px 20px', cursor: 'pointer', fontFamily: fonts.body, fontWeight: '600' },
  cancelBtn: { backgroundColor: colors.textMedium, color: colors.white, border: 'none', padding: '10px 20px', cursor: 'pointer', fontFamily: fonts.body },
  loadingPage: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontFamily: fonts.body },

  membersTable: { backgroundColor: colors.white, border: `1px solid ${colors.peach}`, overflow: 'hidden' },
  memberRow: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0', padding: '12px 20px', borderBottom: `1px solid ${colors.peach}`, alignItems: 'center' },
  memberCell: { fontFamily: fonts.body, fontSize: '13px', color: colors.textMedium, wordBreak: 'break-all' },
  freeBadge: { backgroundColor: '#2a7a2a', color: colors.white, fontSize: '10px', fontFamily: fonts.body, fontWeight: '700', padding: '2px 8px', letterSpacing: '1px', textTransform: 'uppercase' },
  activateBtn: { backgroundColor: colors.navy, color: colors.white, border: 'none', padding: '5px 12px', fontSize: '12px', fontFamily: fonts.body, cursor: 'pointer', fontWeight: '600' },
  deleteMemberBtn: { backgroundColor: '#c64545', color: colors.white, border: 'none', padding: '5px 12px', fontSize: '12px', fontFamily: fonts.body, cursor: 'pointer' },
};
